import { Header } from "@tanstack/react-table";
import { isValid, parse, parseISO } from "date-fns";
import React, { useState } from "react";
import Select from "react-select";

import { DateRangePicker, MultiDatePicker } from "../..";
import {
  customSelectStyles,
  dateRangeOptions,
} from "../../../shared/Constants";
import { SelectField } from "../../../types";
import { CustomDropdownWithCalendar } from "../../shared/CustomSelectComponents";

type DateRangeSelectProps = {
  placeholderText: string;
  header: Header<Record<string, unknown>, unknown>;
};

export const filterDateColumn = (
  row: { getValue: (arg0: any) => any },
  columnId: string,
  filterValue: string | any[],
): any => {
  const cellValue = row.getValue(columnId);

  if (typeof filterValue === "string") {
    if (filterValue.includes(" - ")) {
      const dateRange = filterValue.split(" - ");
      const dates = dateRange.map((date) =>
        parse(date, "M/d/yyyy", new Date()).toISOString(),
      );
      const [start, end] = dates;
      if (cellValue >= start && cellValue <= end) {
        return true;
      } else {
        return false;
      }
    } else if (
      cellValue !== parse(filterValue, "M/d/yyyy", new Date()).toISOString()
    ) {
      return false;
    } else {
      return true;
    }
  } else if (Array.isArray(filterValue)) {
    if (filterValue.length === 1) {
      const date = filterValue[0].toISOString();
      if (cellValue !== date) {
        return false;
      }
    } else if (filterValue.length === 2) {
      const [start, end] = filterValue;
      const cellDate = parseISO(cellValue);
      if (start && !end) {
        return cellDate.getTime() >= start.getTime();
      } else if (start && end) {
        const dates = filterValue.map((date: any) => date.toISOString());
        const startValue = start.toISOString();
        const endValue = end.toISOString();
        if (cellValue >= startValue && cellValue <= endValue) {
          return true;
        } else if (startValue >= endValue && dates.includes(cellValue)) {
          return true;
        } else {
          return false;
        }
      }
    } else if (filterValue.length > 2) {
      const multipleDates = filterValue.map((date: any) => date.toISOString());
      if (multipleDates.includes(cellValue)) {
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  }
};

/**
 * Parses a date range string (e.g., "10/1/2024 - 10/31/2024") into start and end dates.
 * Returns null if the string is invalid.
 */
const parseDateRangeString = (
  dateRangeStr: string,
): { start: Date; end: Date } | null => {
  const parts = dateRangeStr.split(" - ");
  if (parts.length !== 2) return null;

  const start = parse(parts[0], "M/d/yyyy", new Date());
  const end = parse(parts[1], "M/d/yyyy", new Date());

  if (!isValid(start) || !isValid(end)) return null;
  return { start, end };
};

/**
 * Checks if two date ranges overlap.
 * Overlap exists when: start1 <= end2 AND start2 <= end1
 */
const dateRangesOverlap = (
  cellStart: Date,
  cellEnd: Date,
  filterStart: Date,
  filterEnd: Date,
): boolean => {
  return cellStart <= filterEnd && filterStart <= cellEnd;
};

/**
 * Checks if a single date falls within a date range.
 */
const dateWithinRange = (
  date: Date,
  rangeStart: Date,
  rangeEnd: Date,
): boolean => {
  return date >= rangeStart && date <= rangeEnd;
};

/**
 * Handles string filter values for date range column filtering.
 */
const handleStringFilterValue = (
  filterValue: string,
  cellStart: Date,
  cellEnd: Date,
): boolean => {
  if (filterValue.includes(" - ")) {
    const filterRange = parseDateRangeString(filterValue);
    if (!filterRange) return false;
    return dateRangesOverlap(
      cellStart,
      cellEnd,
      filterRange.start,
      filterRange.end,
    );
  }
  const filterDate = parse(filterValue, "M/d/yyyy", new Date());
  return dateWithinRange(filterDate, cellStart, cellEnd);
};

/**
 * Handles array filter values for date range column filtering.
 */
const handleArrayFilterValue = (
  filterValue: Date[],
  cellStart: Date,
  cellEnd: Date,
): boolean => {
  if (filterValue.length === 0) return true;

  if (filterValue.length === 1) {
    return dateWithinRange(filterValue[0], cellStart, cellEnd);
  }

  if (filterValue.length === 2) {
    const [filterStart, filterEnd] = filterValue;
    if (filterStart && !filterEnd) {
      return cellEnd >= filterStart;
    }
    if (filterStart && filterEnd) {
      return dateRangesOverlap(cellStart, cellEnd, filterStart, filterEnd);
    }
  }

  // Multiple individual dates - check if any fall within the cell's range
  return filterValue.some((date) => dateWithinRange(date, cellStart, cellEnd));
};

/**
 * Filter function for columns containing date range strings (e.g., "10/1/2024 - 10/31/2024").
 * Checks if the cell's date range overlaps with the selected filter date range.
 */
export const filterDateRangeColumn = (
  row: { getValue: (arg0: any) => any },
  columnId: string,
  filterValue: string | any[],
): boolean => {
  const cellValue = row.getValue(columnId);
  if (!cellValue || typeof cellValue !== "string") return false;

  const cellRange = parseDateRangeString(cellValue);
  if (!cellRange) return false;

  if (typeof filterValue === "string") {
    return handleStringFilterValue(filterValue, cellRange.start, cellRange.end);
  }

  if (Array.isArray(filterValue)) {
    return handleArrayFilterValue(filterValue, cellRange.start, cellRange.end);
  }

  return true;
};

export const DateFilter: React.FC<DateRangeSelectProps> = ({
  placeholderText,
  header,
}: DateRangeSelectProps) => {
  const [dateFilter, setDateFilter] = useState<string>("");
  const [startDate, setStartDate] = useState<null | Date>(null);
  const [endDate, setEndDate] = useState<null | Date>(null);
  const [selectedDates, setSelectedDates] = useState<any[]>([]);

  const [optionDisplayed, setOptionDisplayed] = useState<SelectField>({
    value: "",
    label: "",
  });

  const column = header?.column;

  return (
    <div className="flex ml-2">
      <div className="w-40" data-testid="date-filter-container">
        <Select
          name="date-filter"
          aria-labelledby="date-filter-aria-label"
          inputId="date-filter"
          id="date-filter-select"
          classNamePrefix="date-filter-select"
          options={[{ value: "", label: "All Dates" }, ...dateRangeOptions]}
          value={optionDisplayed.value !== "" ? optionDisplayed : null}
          isSearchable={false}
          styles={customSelectStyles}
          components={{
            DropdownIndicator: CustomDropdownWithCalendar,
            IndicatorSeparator: () => null,
          }}
          placeholder={placeholderText}
          onChange={(option) => {
            if (option) {
              setDateFilter(option.value);
              setOptionDisplayed(option as SelectField);
              if (option.value === "Custom Range") {
                column?.setFilterValue("");
              } else if (option.value === "Individual Dates") {
                column?.setFilterValue("");
              } else {
                column?.setFilterValue(option.value);
              }
            }
          }}
        />
      </div>
      {dateFilter === "Custom Range" && (
        <div className="ml-2 w-64">
          <DateRangePicker
            placeholderText="Select Range"
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            column={column}
            setDateFilter={setDateFilter}
            setOptionDisplayed={setOptionDisplayed}
          />
        </div>
      )}
      {dateFilter === "Individual Dates" && (
        <div className="ml-2 w-[228px]">
          <MultiDatePicker
            selectedDates={selectedDates}
            setSelectedDates={setSelectedDates}
            column={column}
            setDateFilter={setDateFilter}
            setOptionDisplayed={setOptionDisplayed}
          />
        </div>
      )}
    </div>
  );
};
